import { KeyValuePipe } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faEraser, faMagnifyingGlass, faPlus } from '@fortawesome/free-solid-svg-icons';
import { takeUntil } from 'rxjs/operators';
import { DialogContactsData } from 'src/app/components/dialogs/contacts/models/dialog-contacts-data';
import { ContactPropertyCodes } from 'src/app/core/constantes/contact-property-codes';
import { CustomerPropertyCodes } from 'src/app/core/constantes/customer-property-codes';
import { SortDirections } from 'src/app/core/constantes/sort-directions';
import { UserPropertyCodes } from 'src/app/core/constantes/user-property-codes';
import { BaseDirective } from 'src/app/core/directives/base.directive';
import { TableFilterHandler } from 'src/app/core/table-filter-handler';
import { ComparaisonHelper } from 'src/app/helpers/comparaison-helper';
import { ColumnType } from 'src/app/models/enums/column-type';
import { ContactType } from 'src/app/models/enums/contact-type';
import { FilterType } from 'src/app/models/enums/filter-type';
import { PropertyType } from 'src/app/models/enums/property-type';
import { IAllSelectable } from 'src/app/models/interfaces/all-selectable.interface';
import { IFilterable } from 'src/app/models/interfaces/filterable.interface';
import { SelectionContext } from 'src/app/models/selection-context';
import { ColumnDefinition } from 'src/app/models/server/column-definition';
import { Contact } from 'src/app/models/server/contact';
import { FilterInfos } from 'src/app/models/server/core/filter-infos';
import { SortInfos } from 'src/app/models/server/core/sort-infos';
import { Customer } from 'src/app/models/server/customer';
import { User } from 'src/app/models/server/user';
import { ContactsService } from 'src/app/services/http/contacts.service';
import { CustomersService } from 'src/app/services/http/customers.service';
import { UsersService } from 'src/app/services/http/users.service';
import { FormFocusDirective } from '../../../core/directives/form-focus.directive';
import { ContactDataType } from './models/contact-data-type';

@Component({
    selector: 'app-contacts',
    templateUrl: './contacts.component.html',
    styleUrls: ['./contacts.component.scss'],
    imports: [
        MatCardModule,
        MatFormFieldModule,
        MatSelectModule,
        MatTooltipModule,
        MatOptionModule,
        FormsModule,
        FormFocusDirective,
        ReactiveFormsModule,
        FaIconComponent,
        MatInputModule,
        MatButtonModule,
        MatSortModule,
        MatChipsModule,
        MatIconModule,
        MatDialogClose,
        KeyValuePipe,
    ]
})
export class ContactsComponent extends BaseDirective implements OnInit, OnDestroy, IAllSelectable, IFilterable {
    public resetFilterIcon = faEraser;
    public resetSearchIcon = faEraser;
    public searchIcon = faMagnifyingGlass;
    public addIcon = faPlus;

    /**
     * Enum des types de contact
     */
    public ContactsType = ContactType;
    public ContactDataType = ContactDataType;

    /**
     * Liste des types de contacts avec le nom à utiliser dans les labels
     */
    public contactsTypeLabels: Map<string, string> = new Map<string, string>();

    /**
     * Type de contact sélectionné
     */
    public contactTypeSelected: ContactType = null;

    /**
     * Liste de tous les clients de l'organisation.
     */
    public customersRaw: Customer[];

    /**
     * Liste des clients de l'organisation respectant les filtres.
     */
    public filteredCustomers: Customer[] = [];

    /**
     * Definitions des colonnes du tableau des contacts.
     */
    public contactsTableColumnsDefinitions: ColumnDefinition[] = [];

    /**
     * Liste de tous les utilisateurs de l'organisation.
     */
    public usersRaw: User[];

    /**
     * Liste des utilisateurs de l'organisation respectant les filtres.
     */
    public filteredUsers: User[] = [];

    /**
     * Liste de tous les contacts de l'organisation.
     */
    public contactsRaw: Contact[];

    /**
     * Liste des contacts de l'organisation respectant les filtres.
     */
    public filteredContacts: Contact[] = [];

    /**
     * Gestionnaire des filtres de l'en-tête de la section.
     */
    public searchFilterHandler: TableFilterHandler;

    /**
     * Valeur du filtre courant.
     */
    public currentSort: Sort = { active: ContactPropertyCodes.NAME, direction: SortDirections.ASC };

    /**
     * Contexte de la selection du tableau des contacts.
     */
    public selectionContext: SelectionContext;

    /**
     * Constructeur.
     * @param customersService
     * @param usersService
     * @param contactsTableRef
     * @param cdRef
     * @param dialogRef
     * @param data
     * @param datePipe
     * @param displayLabelHelper
     */
    constructor(
        private customersService: CustomersService,
        private usersService: UsersService,
        private contactsService: ContactsService,
        private contactsTableRef: ElementRef,
        private cdRef: ChangeDetectorRef,
        public dialogRef: MatDialogRef<ContactsComponent>,
        @Inject(MAT_DIALOG_DATA) public data: DialogContactsData
    ) {
        super();

        // Libellés des types de contacts
        this.contactsTypeLabels.set(ContactType.Contact.toString(), 'Contacts');
        this.contactsTypeLabels.set(ContactType.User.toString(), 'Utilisateurs');
        this.contactsTypeLabels.set(ContactType.Customer.toString(), 'Clients');

        this.searchFilterHandler = new TableFilterHandler(this);
        this.selectionContext = new SelectionContext(this.cdRef, this);

        this.searchFilterHandler.initializeFilters([
            {
                propertyCode: 'search',
                header: 'search',
                type: PropertyType.Text,
                displayFormat: null,
                size: 0,
                values: null,
                custom: false,
                isStickyColumn: false,
            },
        ]);
    }

    /**
     * Appelée à l'initialisation du composant.
     */
    ngOnInit(): void {
        this.contactTypeChange(ContactType.Contact);
    }

    /**
     * Appelée lorsque le composant est détruit.
     */
    override ngOnDestroy(): void {
        super.ngOnDestroy();

        if (this.searchFilterHandler) {
            this.searchFilterHandler.dispose();
        }
    }

    /**
     * Retourne les Ids de tous les clients filtrés.
     * Implementation de IAllSelectable.
     * @returns Une liste d'identifiant de clients.
     */
    public getAllIds(): number[] {
        return this.filteredCustomers.map((customer: Customer, index: number) => index);
    }

    /**
     * Applique les filtres de colonnes et de recherche ainsi que le tri sur la liste.
     */
    public applyFilters() {
        switch (this.contactTypeSelected) {
            case ContactType.Customer:
                if (!!this.customersRaw) {
                    const datas = this.customersRaw.slice();
                    this.filteredCustomers = datas.filter((x: Customer) => this.customerIsMatchFilters(x));
                    this.sort();
                }
                break;

            case ContactType.User:
                if (!!this.usersRaw) {
                    const datas = this.usersRaw.slice();
                    this.filteredUsers = datas.filter((x: User) => this.userIsMatchFilters(x));
                    this.sort();
                }
                break;

            case ContactType.Contact:
                if (!!this.contactsRaw) {
                    const datas = this.contactsRaw.slice();
                    this.filteredContacts = datas.filter((x: Contact) => this.contactIsMatchFilters(x));
                    this.sort();
                }
                break;
        }
    }

    /**
     * Charge les clients en mémoire.
     */
    private loadCustomers() {
        let filter: FilterInfos[] = [
            {
                columnName: this.data.dataType === ContactDataType.Email ? CustomerPropertyCodes.EMAIL : CustomerPropertyCodes.MOBILE_NUMBER,
                filterType: FilterType.TextInequality,
                value: '',
                type: ColumnType.Text,
                selectionMultiple: false,
            },
        ];

        this.customersService
            .List(null, filter, null)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((x) => {
                this.customersRaw = x.value;
                this.sort();
                this.applyFilters();
            });
    }

    private loadUsers() {
        let filter: FilterInfos[] = [
            {
                columnName: this.data.dataType === ContactDataType.Email ? UserPropertyCodes.EMAIL : UserPropertyCodes.MOBILE_NUMBER,
                filterType: FilterType.TextInequality,
                value: '',
                type: ColumnType.Text,
                selectionMultiple: false,
            },
        ];

        this.usersService
            .List(null, filter, null)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((x) => {
                this.usersRaw = x.value;
                this.sort();
                this.applyFilters();
            });
    }

    private loadContacts() {
        let filter: FilterInfos[] = [
            {
                columnName: this.data.dataType === ContactDataType.Email ? ContactPropertyCodes.EMAIL : ContactPropertyCodes.MOBILE_NUMBER,
                filterType: FilterType.TextInequality,
                value: '',
                type: ColumnType.Text,
                selectionMultiple: false,
            },
        ];

        this.contactsService
            .List(null, filter, null)
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((x) => {
                this.contactsRaw = x.value;
                this.sort();
                this.applyFilters();
            });
    }

    /**
     * Trie les clients.
     */
    private sort(): void {
        switch (this.contactTypeSelected) {
            case ContactType.Customer:
                const customers = this.filteredCustomers.slice();
                this.filteredCustomers = customers.sort((a, b) => {
                    const isAsc = this.currentSort.direction === SortDirections.ASC;

                    switch (this.currentSort.active) {
                        case CustomerPropertyCodes.CODE: {
                            return ComparaisonHelper.compare(a.code, b.code, isAsc);
                        }
                        case CustomerPropertyCodes.NAME: {
                            return ComparaisonHelper.compare(a.name, b.name, isAsc);
                        }
                        case CustomerPropertyCodes.EMAIL: {
                            return ComparaisonHelper.compare(a.email, b.email, isAsc);
                        }
                        case CustomerPropertyCodes.MOBILE_NUMBER: {
                            return ComparaisonHelper.compare(a.mobileNumber, b.mobileNumber, isAsc);
                        }
                        case CustomerPropertyCodes.SIRET: {
                            return ComparaisonHelper.compare(a.siret, b.siret, isAsc);
                        }
                        default: {
                            return ComparaisonHelper.compare(a.name, b.name, isAsc);
                        }
                    }
                });
                break;

            case ContactType.User:
                const users = this.filteredUsers.slice();
                this.filteredUsers = users.sort((a, b) => {
                    const isAsc = this.currentSort.direction === SortDirections.ASC;

                    switch (this.currentSort.active) {
                        case UserPropertyCodes.LAST_NAME: {
                            return ComparaisonHelper.compare(a.lastName, b.lastName, isAsc);
                        }
                        case UserPropertyCodes.FIRST_NAME: {
                            return ComparaisonHelper.compare(a.firstName, b.firstName, isAsc);
                        }
                        case UserPropertyCodes.DISPLAY_NAME: {
                            return ComparaisonHelper.compare(a.displayName, b.displayName, isAsc);
                        }
                        case UserPropertyCodes.EMAIL: {
                            return ComparaisonHelper.compare(a.email, b.email, isAsc);
                        }
                        case UserPropertyCodes.MOBILE_NUMBER: {
                            return ComparaisonHelper.compare(a.phoneNumber, b.phoneNumber, isAsc);
                        }
                        default: {
                            return ComparaisonHelper.compare(a.lastName, b.lastName, isAsc);
                        }
                    }
                });
                break;
            case ContactType.Contact:
                const contacts = this.filteredContacts.slice();
                this.filteredContacts = contacts.sort((a, b) => {
                    const isAsc = this.currentSort.direction === SortDirections.ASC;

                    switch (this.currentSort.active) {
                        case ContactPropertyCodes.COMPANY_NAME: {
                            return ComparaisonHelper.compare(a.companyName, b.companyName, isAsc);
                        }
                        case ContactPropertyCodes.FIRST_NAME: {
                            return ComparaisonHelper.compare(a.firstName, b.firstName, isAsc);
                        }
                        case ContactPropertyCodes.LAST_NAME: {
                            return ComparaisonHelper.compare(a.lastName, b.lastName, isAsc);
                        }
                        case ContactPropertyCodes.EMAIL: {
                            return ComparaisonHelper.compare(a.email, b.email, isAsc);
                        }
                        case ContactPropertyCodes.MOBILE_NUMBER: {
                            return ComparaisonHelper.compare(a.mobileNumber, b.mobileNumber, isAsc);
                        }
                        default: {
                            return ComparaisonHelper.compare(a.lastName, b.lastName, isAsc);
                        }
                    }
                });
                break;
        }
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Annuler'.
     */
    public cancel(): void {
        this.dialogRef.close();
    }

    /**
     * Appelée lorsque l'utilisateur clique sur l'en-tête d'une colonne du tableau des clients.
     * @param sort : information sur le tri.
     */
    public onSorting(sort: Sort) {
        this.currentSort = sort;
        this.sort();
    }

    /**
     * Appelée lorsque l'utilisateur clique sur un élément de la liste.
     * @param event : Evenment clic de la souris.
     * @param index : Index de l'élément cliqué.
     */
    public onMouseClick(event: MouseEvent, index: number) {
        this.selectionContext.onMouseClick(event, index);
    }

    /**
     * Appelée lorsque l'utilisateur appuie sur une touche du clavier.
     * @param event Événement du clavier.
     */
    @HostListener('document:keydown', ['$event'])
    private handleKeyboardEvent(event: KeyboardEvent) {
        if (event.cancelBubble === false) {
            this.selectionContext.handleKeyboardEvent(event);
        }
    }

    /**
     * Appelée lorsque l'utilisateur clique sur un élément quelconque de la page.
     * @param event Evenment clic de la souris.
     */
    @HostListener('document:click', ['$event'])
    private clickout(event) {
        if (this.selectionContext.selections.length && !this.contactsTableRef.nativeElement.contains(event.target)) {
            // Si la selection n'est pas vide et que l'utilisateur clique en dehors du containeur des éléments pouvant être glisser/déplacer, alors vide la selection.
            this.selectionContext.clearSelection();
        }
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Ajouter l’adresse sélectionnée au champ 'TO'
     * Ajoute les adresses e-mail des clients sélectionnés au champ 'Destinataires'.
     */
    public addToAddress(): void {
        this.selectionContext.selections.forEach((index) => {
            if (this.data.dataType === ContactDataType.Email) {
                const email = this.getEmail(index);
                if (email) {
                    if (!this.data.toAddresses.includes(email)) {
                        this.data.toAddresses.push(email);
                    }
                }
            } else {
                const mobileNumber = this.getMobileNumber(index);
                if (mobileNumber) {
                    if (!this.data.toAddresses.includes(mobileNumber)) {
                        this.data.toAddresses.push(mobileNumber);
                    }
                }
            }
        });
    }

    /**
     * Retire l'adresse spécifiée du champ 'Destinataires'.
     * @param address: Adresse email a retirer.
     */
    public removeToAddress(address: string): void {
        this.data.toAddresses.splice(this.data.toAddresses.indexOf(address), 1);
    }

    /**
     * Appelée lorsque l'utilisateur clique sur le bouton 'Ajouter l’adresse sélectionnée au champ 'CC'
     * Ajoute les adresses e-mail des clients sélectionnés au champ 'Copies conformes'.
     */
    public addCcAddress(): void {
        this.selectionContext.selections.forEach((index) => {
            if (!this.data.ccAddresses.includes(this.getEmail(index))) {
                this.data.ccAddresses.push(this.getEmail(index));
            }
        });
    }

    /**
     * Retire l'adresse spécifiée du champ 'Copies conformes'.
     * @param address: Adresse email a retirer.
     */
    public removeCcAddress(address: string): void {
        this.data.ccAddresses.splice(this.data.ccAddresses.indexOf(address), 1);
    }

    /**
     * Appelé lorsque l'utilisateur clique sur le bouton 'Ajouter l’adresse sélectionnée au champ 'CCi''
     * Ajoute les adresses e-mail des clients sélectionnés au champ 'Copies conformes cachées'.
     */
    public addCciAddress(): void {
        this.selectionContext.selections.forEach((index) => {
            if (!this.data.cciAddresses.includes(this.getEmail(index))) {
                this.data.cciAddresses.push(this.getEmail(index));
            }
        });
    }

    /**
     * Retire l'adresse spécifiée du champ 'Copies conformes cachées'.
     * @param address: Adresse email a retirer.
     */
    public removeCciAddress(address: string): void {
        this.data.cciAddresses.splice(this.data.cciAddresses.indexOf(address), 1);
    }

    /**
     * Indique si l'élément specifié est selectionné.
     * @param index : index de l'élément à tester
     * @returns True si l'élément est selectionné, ou false autrement.
     */
    public isSelected(index: number): boolean {
        return this.selectionContext.isSelected(index);
    }

    /**
     * Indique si un client respecte les filtres de colonnes ou de recherche.
     * @param customer : client à tester.
     * @returns True si le client respecte tous les filtres de colonnes et de recherche; false autrement.
     */
    private customerIsMatchFilters(customer: Customer): boolean {
        if (!!this.searchFilterHandler.filterValues['search']) {
            if (customer.code && customer.code.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                return true;
            }

            if (customer.name && customer.name.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                return true;
            }

            if (this.data.dataType === ContactDataType.Email) {
                if (customer.email && customer.email.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                    return true;
                }
            } else if (this.data.dataType === ContactDataType.Sms) {
                if (customer.mobileNumber && customer.mobileNumber.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                    return true;
                }
            }

            if (customer.siret && customer.siret.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                return true;
            }

            return false;
        } else {
            return true;
        }
    }

    /**
     * Indique si un utilisateur respecte les filtres de colonnes ou de recherche.
     * @param user : utilisateur à tester.
     * @returns True si l'utilisateur respecte tous les filtres de colonnes et de recherche; false autrement.
     */
    private userIsMatchFilters(user: User): boolean {
        if (!!this.searchFilterHandler.filterValues['search']) {
            if (user.lastName && user.lastName.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                return true;
            }

            if (user.firstName && user.firstName.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                return true;
            }

            if (this.data.dataType === ContactDataType.Email) {
                if (user.email && user.email.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                    return true;
                }
            } else if (this.data.dataType === ContactDataType.Sms) {
                if (user.mobileNumber && user.mobileNumber.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                    return true;
                }
            }

            return false;
        } else {
            return true;
        }
    }

    /**
     * Indique si un contact respecte les filtres de colonnes ou de recherche.
     * @param contact : contact à tester.
     * @returns True si le contact respecte tous les filtres de colonnes et de recherche; false autrement.
     */
    private contactIsMatchFilters(contact: Contact): boolean {
        if (!!this.searchFilterHandler.filterValues['search']) {
            if (contact.lastName && contact.lastName.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                return true;
            }

            if (contact.firstName && contact.firstName.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                return true;
            }

            if (this.data.dataType === ContactDataType.Email) {
                if (contact.email && contact.email.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                    return true;
                }
            } else if (this.data.dataType === ContactDataType.Sms) {
                if (contact.mobileNumber && contact.mobileNumber.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                    return true;
                }
            }

            if (contact.companyName && contact.companyName.toLowerCase().includes(this.searchFilterHandler.filterValues['search'].values[0].toLowerCase())) {
                return true;
            }

            return false;
        } else {
            return true;
        }
    }

    public contactTypeChange(contactType: ContactType) {
        this.contactTypeSelected = contactType;

        switch (contactType) {
            case ContactType.Customer:
                this.setCustomersTableColumnsDefinitions(this.data.dataType);
                this.loadCustomers();
                break;

            case ContactType.User:
                this.setUsersTableColumnsDefinitions(this.data.dataType);
                this.loadUsers();
                break;

            case ContactType.Contact:
                this.setContactsTableColumnsDefinitions(this.data.dataType);
                this.loadContacts();
                break;
        }
    }

    public onSearch() {
        this.applyFilters();
    }

    private setCustomersTableColumnsDefinitions(dataType: ContactDataType) {
        let dataPropertyCode = '';
        let dataHeader = '';

        switch (dataType) {
            case ContactDataType.Email:
                dataPropertyCode = CustomerPropertyCodes.EMAIL;
                dataHeader = 'Email';
                break;
            case ContactDataType.Sms:
                dataPropertyCode = CustomerPropertyCodes.MOBILE_NUMBER;
                dataHeader = 'N° de mobile';
                break;
        }

        this.contactsTableColumnsDefinitions = [
            {
                propertyCode: CustomerPropertyCodes.CODE,
                header: 'Code',
                type: PropertyType.Text,
                displayFormat: null,
                size: 150,
                values: null,
                custom: false,
                isStickyColumn: false,
            },
            {
                propertyCode: CustomerPropertyCodes.NAME,
                header: 'Nom',
                type: PropertyType.Text,
                displayFormat: null,
                size: null,
                values: null,
                custom: false,
                isStickyColumn: false,
            },
            {
                propertyCode: dataPropertyCode,
                header: dataHeader,
                type: PropertyType.Text,
                displayFormat: null,
                size: null,
                values: null,
                custom: false,
                isStickyColumn: false,
            },
            {
                propertyCode: CustomerPropertyCodes.SIRET,
                header: 'Siret',
                type: PropertyType.Text,
                displayFormat: null,
                size: null,
                values: null,
                custom: false,
                isStickyColumn: false,
            },
        ];
    }

    private setUsersTableColumnsDefinitions(dataType: ContactDataType) {
        let dataPropertyCode = '';
        let dataHeader = '';

        switch (dataType) {
            case ContactDataType.Email:
                dataPropertyCode = UserPropertyCodes.EMAIL;
                dataHeader = 'Email';
                break;
            case ContactDataType.Sms:
                dataPropertyCode = UserPropertyCodes.MOBILE_NUMBER;
                dataHeader = 'N° de mobile';
                break;
        }

        this.contactsTableColumnsDefinitions = [
            {
                propertyCode: UserPropertyCodes.LAST_NAME,
                header: 'Nom',
                type: PropertyType.Text,
                displayFormat: null,
                size: 150,
                values: null,
                custom: false,
                isStickyColumn: false,
            },
            {
                propertyCode: UserPropertyCodes.FIRST_NAME,
                header: 'Prénom',
                type: PropertyType.Text,
                displayFormat: null,
                size: null,
                values: null,
                custom: false,
                isStickyColumn: false,
            },
            {
                propertyCode: UserPropertyCodes.DISPLAY_NAME,
                header: 'DisplayName',
                type: PropertyType.Text,
                displayFormat: null,
                size: null,
                values: null,
                custom: false,
                isStickyColumn: false,
            },
            {
                propertyCode: dataPropertyCode,
                header: dataHeader,
                type: PropertyType.Text,
                displayFormat: null,
                size: null,
                values: null,
                custom: false,
                isStickyColumn: false,
            },
        ];
    }

    private setContactsTableColumnsDefinitions(dataType: ContactDataType) {
        let dataPropertyCode = '';
        let dataHeader = '';

        switch (dataType) {
            case ContactDataType.Email:
                dataPropertyCode = ContactPropertyCodes.EMAIL;
                dataHeader = 'Email';
                break;
            case ContactDataType.Sms:
                dataPropertyCode = ContactPropertyCodes.MOBILE_NUMBER;
                dataHeader = 'N° de mobile';
                break;
        }

        this.contactsTableColumnsDefinitions = [
            {
                propertyCode: ContactPropertyCodes.LAST_NAME,
                header: 'Nom',
                type: PropertyType.Text,
                displayFormat: null,
                size: 150,
                values: null,
                custom: false,
                isStickyColumn: false,
            },
            {
                propertyCode: ContactPropertyCodes.FIRST_NAME,
                header: 'Prénom',
                type: PropertyType.Text,
                displayFormat: null,
                size: null,
                values: null,
                custom: false,
                isStickyColumn: false,
            },
            {
                propertyCode: dataPropertyCode,
                header: dataHeader,
                type: PropertyType.Text,
                displayFormat: null,
                size: null,
                values: null,
                custom: false,
                isStickyColumn: false,
            },
            {
                propertyCode: ContactPropertyCodes.COMPANY_NAME,
                header: 'Société',
                type: PropertyType.Text,
                displayFormat: null,
                size: null,
                values: null,
                custom: false,
                isStickyColumn: false,
            },
        ];
    }

    private getEmail(index: number): string {
        switch (this.contactTypeSelected) {
            case ContactType.Customer:
                return this.filteredCustomers[index].email;

            case ContactType.User:
                return this.filteredUsers[index].email;

            case ContactType.Contact:
                return this.filteredContacts[index].email;
        }
    }

    private getMobileNumber(index: number): string {
        switch (this.contactTypeSelected) {
            case ContactType.Customer:
                return this.filteredCustomers[index].mobileNumber;

            case ContactType.User:
                return this.filteredUsers[index].mobileNumber;

            case ContactType.Contact:
                return this.filteredContacts[index].mobileNumber;
        }
    }
}
